// Third-party
import React from 'react';

// Components
import DefaultModal from '../DefaultModal';
import { PrimaryLink } from '../../buttons';

// Constants and types
import { MODAL_SIZES } from '@/utils/constants';
import ComparisonItem from '@/types/comparison-item';
import User from '@/types/user';

// Utils
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { ProposalExportRequirementsModalDisplayMode } from '@/enums/Comparison';

type Props = {
  onCancel: () => void;
  onClosed: () => void;
  onConfirm: () => void;
  onGoToSuitability: () => void;
  portfolioNamesOutsideRiskProfileRange?: string[];
  type: ProposalExportRequirementsModalDisplayMode[];
  items: ComparisonItem[];
  user: User;
  show: boolean;
};

const ProposalExportRequirementsModal = (props: Props) => {
  const { onCancel, onClosed, onConfirm, onGoToSuitability, portfolioNamesOutsideRiskProfileRange, type, show } = props;

  let icon: React.ReactNode = '';
  const hideCancelButton = false;
  let confirmActionLabel;
  const bodyText: React.ReactNode[] = [];
  let securitiesWarningText: React.ReactNode = '';

  const allowedToExport = [ProposalExportRequirementsModalDisplayMode.WARNING, ProposalExportRequirementsModalDisplayMode.SECURITY_WARNING];

  const onlyWarningsOrEmpty = type.every(displayMode => allowedToExport.includes(displayMode)) || type.length === 0;
  const cancelLabel = onlyWarningsOrEmpty ? undefined : formatMessage('Modal.PortfolioReadOnly.Footer.Actions.CancelLabel');
  const hiddenPrimaryButton = !onlyWarningsOrEmpty;

  if (type.includes(ProposalExportRequirementsModalDisplayMode.ERROR)) {
    icon = <i className="icon-exclamation" />;
    bodyText.push(
      <WarningCard
        body={
          <div className="suitability-warning-card">
            <FormattedMessage id="ProposalExportRequirementsModal.Subtitle.Error" />
            <PrimaryLink className="suitability-link" onClick={onGoToSuitability}>
              <FormattedMessage id="ProposalExportRequirementsModal.Subtitle.SuitabilityWarning.GoToSuitability" />
            </PrimaryLink>
          </div>
        }
        icon={icon}
        title={<FormattedMessage id="ProposalExportRequirementsModal.Subtitle.SuitabilityWarning.Title" />}
      />,
    );
  }
  if (type.includes(ProposalExportRequirementsModalDisplayMode.WARNING)) {
    icon = <i className="icon-exclamation" />;
    confirmActionLabel = formatMessage('ProposalExportRequirementsModal.Confirm');
    bodyText.push(
      <WarningCard
        body={
          <div className="suitability-warning-card">
            <FormattedMessage id="ProposalExportRequirementsModal.Subtitle.Warning" />
            <PrimaryLink className="suitability-link" onClick={onGoToSuitability}>
              <FormattedMessage id="ProposalExportRequirementsModal.Subtitle.SuitabilityWarning.GoToSuitability" />
            </PrimaryLink>
          </div>
        }
        icon={icon}
        title={<FormattedMessage id="ProposalExportRequirementsModal.Subtitle.SuitabilityWarning.Title" />}
      />,
    );
  }
  if (type.includes(ProposalExportRequirementsModalDisplayMode.SECURITY_WARNING)) {
    icon = <i className="icon-ds-warning" />;
    securitiesWarningText = (
      <WarningCard
        body={
          <div className="securities-warning-card">
            <FormattedMessage id="ProposalExportRequirementsModal.Subtitle.SecuritiesWarning.Text1" />
            <FormattedMessage tagName="p" id="ProposalExportRequirementsModal.Subtitle.SecuritiesWarning.Text2" />
          </div>
        }
        icon={icon}
        title={<FormattedMessage id="ProposalExportRequirementsModal.Subtitle.SecuritiesWarning.Title" />}
      />
    );
  }
  if (type.includes(ProposalExportRequirementsModalDisplayMode.MISSING_COMPARISON_RISK_PROFILE)) {
    icon = <i className="icon-ds-warning" />;

    bodyText.push(
      <WarningCard
        body={
          <div className="suitability-warning-card">
            <FormattedMessage id="ProposalExportRequirementsModal.Subtitle.MissingComparisonRiskProfile.Text1" />
            <FormattedMessage tagName="p" id="ProposalExportRequirementsModal.Subtitle.MissingComparisonRiskProfile.Text2" />
          </div>
        }
        icon={icon}
        title={<FormattedMessage id="ProposalExportRequirementsModal.Subtitle.MissingComparisonRiskProfile.Title" />}
      />,
    );
  }
  if (type.includes(ProposalExportRequirementsModalDisplayMode.PORTFOLIO_RISK_PROFILE_OUTSIDE_RANGE)) {
    icon = <i className="icon-ds-warning" />;

    bodyText.push(
      <WarningCard
        body={
          <div className="suitability-warning-card">
            <FormattedMessage id="ProposalExportRequirementsModal.Subtitle.PortfolioOutsideRange.Text1" />
            <FormattedMessage tagName="p" id="ProposalExportRequirementsModal.Subtitle.PortfolioOutsideRange.Text2" />
            {portfolioNamesOutsideRiskProfileRange &&
              portfolioNamesOutsideRiskProfileRange.length > 0 &&
              portfolioNamesOutsideRiskProfileRange.map((portfolioName, index) => (
                <div className="item-name" key={index}>
                  {portfolioName}
                </div>
              ))}
          </div>
        }
        icon={icon}
        title={<FormattedMessage id="ProposalExportRequirementsModal.Subtitle.PortfolioOutsideRange.Title" />}
      />,
    );
  }
  if (type.includes(ProposalExportRequirementsModalDisplayMode.RECOMMENDED_PORTFOLIOS_OUTSIDE_RANGE)) {
    icon = <i className="icon-ds-warning" />;

    bodyText.push(
      <WarningCard
        body={
          <div className="suitability-warning-card">
            <FormattedMessage id="ProposalExportRequirementsModal.Subtitle.RecommendedPortfolioOutsideRange.Text1" />
            <FormattedMessage tagName="p" id="ProposalExportRequirementsModal.Subtitle.RecommendedPortfolioOutsideRange.Text2" />
            {portfolioNamesOutsideRiskProfileRange &&
              portfolioNamesOutsideRiskProfileRange.length > 0 &&
              portfolioNamesOutsideRiskProfileRange.map((portfolioName, index) => (
                <div className="item-name" key={index}>
                  {portfolioName}
                </div>
              ))}
          </div>
        }
        icon={icon}
        title={<FormattedMessage id="ProposalExportRequirementsModal.Subtitle.RecommendedPortfolioOutsideRange.Title" />}
      />,
    );
  }
  if (type.includes(ProposalExportRequirementsModalDisplayMode.MISSING_PORTFOLIO_RISK_PROFILE)) {
    icon = <i className="icon-ds-warning" />;

    bodyText.push(
      <WarningCard
        body={
          <div className="suitability-warning-card">
            <FormattedMessage id="ProposalExportRequirementsModal.Subtitle.MissingPortfolioRiskProfile.Text1" />
            <FormattedMessage tagName="p" id="ProposalExportRequirementsModal.Subtitle.MissingPortfolioRiskProfile.Text2" />
          </div>
        }
        icon={icon}
        title={<FormattedMessage id="ProposalExportRequirementsModal.Subtitle.MissingPortfolioRiskProfile.Title" />}
      />,
    );
  }
  if (type.includes(ProposalExportRequirementsModalDisplayMode.MISSING_RECOMMENDED_PORTFOLIOS_RISK_PROFILE)) {
    icon = <i className="icon-ds-warning" />;

    bodyText.push(
      <WarningCard
        body={
          <div className="suitability-warning-card">
            <FormattedMessage id="ProposalExportRequirementsModal.Subtitle.MissingRecommendedPortfoliosRiskProfile.Text1" />
            <FormattedMessage tagName="p" id="ProposalExportRequirementsModal.Subtitle.MissingRecommendedPortfoliosRiskProfile.Text2" />
          </div>
        }
        icon={icon}
        title={<FormattedMessage id="ProposalExportRequirementsModal.Subtitle.MissingRecommendedPortfoliosRiskProfile.Title" />}
      />,
    );
  }
  if (type.includes(ProposalExportRequirementsModalDisplayMode.PORTFOLIO_NOT_CURRENT_OR_RECOMMENDED)) {
    icon = <i className="icon-ds-warning" />;

    bodyText.push(
      <WarningCard
        body={
          <div className="suitability-warning-card">
            <FormattedMessage tagName="p" id="ProposalExportRequirementsModal.Subtitle.PortfoliosCurrentOrRecommended.Text2" />
          </div>
        }
        icon={icon}
        title={<FormattedMessage id="ProposalExportRequirementsModal.Subtitle.PortfoliosCurrentOrRecommended.Title" />}
      />,
    );
  }
  if (type.includes(ProposalExportRequirementsModalDisplayMode.RECOMMENDED_PORTFOLIOS_EXCEEDS_CURRENT_OFF_SHELF)) {
    icon = <i className="icon-ds-warning" />;

    bodyText.push(
      <WarningCard
        body={
          <div className="suitability-warning-card">
            <FormattedMessage tagName="p" id="ProposalExportRequirementsModal.Subtitle.RecommendedPortfoliosExceedsCurrentOffShelf.Text2" />
          </div>
        }
        icon={icon}
        title={<FormattedMessage id="ProposalExportRequirementsModal.Subtitle.RecommendedPortfoliosExceedsCurrentOffShelf.Title" />}
      />,
    );
  }
  if (type.includes(ProposalExportRequirementsModalDisplayMode.RECOMMENDED_PORTFOLIOS_CONTAINS_OFF_SHELF)) {
    icon = <i className="icon-ds-warning" />;

    bodyText.push(
      <WarningCard
        body={
          <div className="suitability-warning-card">
            <FormattedMessage tagName="p" id="ProposalExportRequirementsModal.Subtitle.RecommendedPortfoliosContainsOffShelf.Text2" />
          </div>
        }
        icon={icon}
        title={<FormattedMessage id="ProposalExportRequirementsModal.Subtitle.RecommendedPortfoliosContainsOffShelf.Title" />}
      />,
    );
  }

  const body = (
    <>
      <div className="body-intro-text">
        <FormattedMessage id="ProposalExportRequirementsModal.Intro" />
      </div>
      {securitiesWarningText}
      {bodyText}
    </>
  );

  return (
    <DefaultModal
      actionsInverted
      body={body}
      className="proposal-export-requirements-modal"
      confirmLabel={confirmActionLabel}
      hideCancelButton={hideCancelButton}
      onCancel={onCancel}
      onClosed={onClosed}
      onConfirm={onConfirm}
      show={show}
      size={MODAL_SIZES.SM}
      title={<FormattedMessage id="ProposalExportRequirementsModal.Title" />}
      hidePrimaryButton={hiddenPrimaryButton}
      {...(cancelLabel ? { cancelLabel } : {})}
    />
  );
};

type WarningProps = {
  icon: React.ReactNode;
  title: React.ReactNode;
  body: React.ReactNode;
};

const WarningCard: React.FC<WarningProps> = ({ icon, title, body }) => (
  <div className="warning-card">
    <div className="warning-header">
      <div className="icon">{icon}</div>
      <div className="title">{title}</div>
    </div>
    <div className="warning-body">{body}</div>
  </div>
);

export default ProposalExportRequirementsModal;
